import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronRight, ChevronDown, ChevronLeft,
  Folder, Menu, Moon, Sun, Maximize2, Minimize2, BookOpen,
} from 'lucide-react';
import { SearchBar, Icon } from '../ui';
import { pageMeta } from '../../config/pages';

// T0 — Shell unifié de la documentation : sidebar (navigation + recherche + thème) + topbar + contenu.
// Remplace l'ancien App.js : dark mode cohérent via la classe `.dark` (cf. tailwind darkMode:'class'),
// SUPPRESSION du hack `isIAPage`/`ia-page-container` qui forçait certaines pages en noir.
// La découverte dynamique des pages (require.context) reste dans App.js et alimente `tree`.

// Nœud d'arbre de navigation (libellés conviviaux via pages.js).
const TreeNode = ({ name, content, path = '', depth = 0, searchTerm = '' }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const location = useLocation();
  const isFile = React.isValidElement(content);
  const hasChildren = !isFile && Object.keys(content).length > 0;
  const isActive = location.pathname === `/${path}${name}`.toLowerCase();
  const label = isFile ? pageMeta(name).title : name;

  if (searchTerm && !label.toLowerCase().includes(searchTerm.toLowerCase())) {
    return null;
  }

  return (
    <div className="select-none">
      <div
        className={`flex items-center py-2 px-3 rounded-lg transition-all duration-200 ${
          isActive
            ? 'bg-blue-500 text-white'
            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
        }`}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
      >
        {hasChildren && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`p-1 rounded-lg mr-1 ${isActive ? 'hover:bg-blue-600' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}
          >
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        )}
        {isFile ? (
          <>
            <Icon name={pageMeta(name).icon} size={16} className="mr-2 shrink-0" />
            <Link to={`/${path}${name}`.toLowerCase()} className={`flex-1 ${isActive ? 'text-white' : 'text-gray-700 dark:text-gray-300'}`}>
              {label}
            </Link>
          </>
        ) : (
          <>
            <Folder size={16} className="mr-2 shrink-0" />
            <span className="flex-1">{label}</span>
          </>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div className="ml-2">
          {Object.entries(content).map(([key, value]) => (
            <TreeNode key={key} name={key} content={value} path={`${path}${name}/`} depth={depth + 1} searchTerm={searchTerm} />
          ))}
        </div>
      )}
    </div>
  );
};

export const PageShell = ({ tree, children }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDark, setIsDark] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Dark mode : pose la classe `.dark` sur <html> (Tailwind darkMode:'class').
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  // Redimensionnement de la sidebar (listeners attachés pendant le drag).
  useEffect(() => {
    if (!isResizing) return undefined;
    const onMove = (e) => {
      const w = e.clientX;
      if (w > 200 && w < window.innerWidth * 0.8) setSidebarWidth(w);
    };
    const onUp = () => setIsResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizing]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  return (
    <div className="h-screen flex bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
      {/* Sidebar */}
      <motion.div
        initial={false}
        animate={{ width: sidebarWidth, x: isOpen ? 0 : -sidebarWidth }}
        className="fixed top-0 bottom-0 left-0 z-40 shadow-lg flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700"
      >
        <div className="h-16 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <BookOpen size={24} className="text-blue-500" />
            <h1 className="text-lg font-bold">ScanBank Docs</h1>
          </div>
          <div className="flex items-center space-x-1">
            <button onClick={() => setIsDark(!isDark)} title="Thème clair/sombre" className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button onClick={toggleFullscreen} title="Plein écran" className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
              {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
            </button>
          </div>
        </div>

        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher une page…" />
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-1">
            {Object.entries(tree).map(([key, value]) => (
              <TreeNode key={key} name={key} content={value} searchTerm={searchTerm} />
            ))}
          </div>
        </div>
      </motion.div>

      {/* Poignée de redimensionnement */}
      {isOpen && (
        <div
          className="fixed top-0 bottom-0 w-1 cursor-ew-resize z-50 hover:bg-blue-500 transition-all"
          style={{ left: sidebarWidth }}
          onMouseDown={() => setIsResizing(true)}
        />
      )}

      {/* Zone principale */}
      <motion.div
        animate={{ marginLeft: isOpen ? sidebarWidth : 0, width: isOpen ? `calc(100% - ${sidebarWidth}px)` : '100%' }}
        className="flex-1"
      >
        <div className="h-16 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center px-6">
          <button onClick={() => setIsOpen(!isOpen)} title="Afficher/Masquer la navigation" className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            {isOpen ? <ChevronLeft size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <div className="p-6 overflow-auto">{children}</div>
      </motion.div>

      {isResizing && <div className="fixed inset-0 pointer-events-none bg-blue-500/10 z-50" />}
    </div>
  );
};

export default PageShell;
